import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Tabs, Tab, Button, Form } from 'react-bootstrap';
import { getCommunityVideosApi, getCommunityWorkbooksApi, communityLiveApi } from '../../../api/communities';
import { ZegoLiveStream } from './ZegoLiveStream';

export const CommunityContent = ({ communityId, isOwner, user, community }) => {
  const [videos, setVideos] = useState([]);
  const [workbooks, setWorkbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [updatingLive, setUpdatingLive] = useState(false);

  useEffect(() => {
    if (community && community.isLive !== undefined) {
      setIsLive(community.isLive);
    }
  }, [community]);

  useEffect(() => {
    fetchContent();
  }, [communityId]);

  const fetchContent = async () => {
    const token = localStorage.getItem('authToken');
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    if (!hasValidToken) {
      setLoading(false);
      setVideos([]);
      setWorkbooks([]);
      return;
    }
    try {
      setLoading(true);
      const [videosRes, workbooksRes] = await Promise.all([
        getCommunityVideosApi(communityId).catch(() => ({ data: [] })),
        getCommunityWorkbooksApi(communityId).catch(() => ({ data: [] }))
      ]);
      setVideos(videosRes.data || []);
      setWorkbooks(workbooksRes.data || []);
    } catch (err) {
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const toggleLiveStatus = async (e) => {
    const newStatus = e.target.checked;
    try {
      setUpdatingLive(true);
      await communityLiveApi(communityId, { isLive: newStatus });
      setIsLive(newStatus);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update live status');
      setIsLive(!newStatus); // revert on failure
    } finally {
      setUpdatingLive(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="community-content mx-auto">
      {isOwner && (
        <Card className="mb-4 shadow-sm border-0 border-start border-4 border-danger rounded-3 bg-light">
          <Card.Body className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1 fw-bold text-gray-900"><i className="bi bi-broadcast text-danger me-2"></i> Live Session</h5>
              <p className="mb-0 text-muted small">Toggle this on when you are hosting a live session for your community.</p>
            </div>
            <Form.Check 
              type="switch"
              id="live-mode-switch"
              className="fs-4"
              checked={isLive}
              onChange={toggleLiveStatus}
              disabled={updatingLive}
            />
          </Card.Body>
        </Card>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {isLive && (
        <div className="mb-4 animate__animated animate__fadeIn">
          <ZegoLiveStream communityId={communityId} user={user} isOwner={isOwner} />
        </div>
      )}

      <Tabs defaultActiveKey="videos" className="mb-4">
        <Tab eventKey="videos" title={<><i className="bi bi-play-btn me-2"></i>Videos</>}>
          {videos.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-4 border-dashed text-muted">
              <i className="bi bi-film fs-1 mb-2"></i>
              <h5>No videos yet</h5>
              <p>Videos added to this community will appear here.</p>
            </div>
          ) : (
            <Row className="g-4">
              {videos.map(video => (
                <Col md={6} lg={4} key={video._id}>
                  <Card className="h-100 shadow-sm border-0 rounded-4 overflow-hidden">
                    <div className="position-relative bg-dark" style={{ height: '220px' }}>
                      {(video.video_link && (video.video_link.includes('youtube.com') || video.video_link.includes('vimeo.com') || video.video_link.includes('youtu.be'))) ? (
                        <iframe 
                          src={video.video_link.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} 
                          className="w-100 h-100 border-0" 
                          allowFullScreen 
                          title={video.title || "Video"}
                        ></iframe>
                      ) : (
                        <video src={video.video_link} controls className="w-100 h-100 object-fit-contain" preload="metadata" />
                      )}
                    </div>
                    <Card.Body>
                      <div className="fs-6 fw-bold mb-1 text-truncate-2">{video.title || 'Untitled Video'}</div>
                      <div className="text-sm text-secondary">{new Date(video.createdAt).toLocaleDateString()}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        <Tab eventKey="workbooks" title={<><i className="bi bi-journal-text me-2"></i>Workbooks</>}>
          {workbooks.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-4 border-dashed text-muted">
              <i className="bi bi-file-earmark-text fs-1 mb-2"></i>
              <h5>No workbooks yet</h5>
              <p>Educational material and workbooks will appear here.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {workbooks.map(workbook => (
                <Card key={workbook._id} className="shadow-sm border-0 rounded-3">
                  <Card.Body className="d-flex align-items-center justify-content-between p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary bg-opacity-10 text-primary p-3 rounded-3 fs-3">
                        <i className="bi bi-file-pdf-fill"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">{workbook.title}</h6>
                        <small className="text-muted">{workbook.description}</small>
                      </div>
                    </div>
                    <Button variant="outline-primary" size="sm" className="rounded-pill px-3" href={workbook.fileUrl} target="_blank">
                      <i className="bi bi-download me-1"></i> Download
                    </Button>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};
